<template>
  <div class="settingsAreaPrivacy">
    <BlockExcludeModal
      v-if="blocking || excluding"
      :isSettings="true"
      :displayName="firstName"
      :blocking="blocking"
      :excluding="excluding"
      :blockingInProcess="blockingInProcess"
      :excludingInProcess="excludingInProcess"
      :breakEndDate="breakEndDate"
      @editExclusion="initEditExclusion"
      @block="block"
      @exclude="updateExclusion"
    />
    <BlockExcludeSuccessModal
      v-if="unexcludingComplete || blockingComplete || excludingComplete"
      :displayName="firstName"
      :unexcludingComplete="unexcludingComplete"
      :blockingComplete="blockingComplete"
      :excludingComplete="excludingComplete"
      @block="block"
    />
    <div class="s__container">
      <div class="s__options">
        <div class="s__option" :class="tab === 'EXCLUDED' ? 's__option-selected' : ''" @click="switchTab('EXCLUDED')">On break</div>
        <div class="s__option" :class="tab === 'BLOCKED' ? 's__option-selected' : ''" @click="switchTab('BLOCKED')">Blocked</div>
      </div>
      <div class="s__desc" v-if="tab === 'EXCLUDED'">List of people you have taken a break from.</div>
      <div class="s__desc" v-if="tab === 'BLOCKED'">List of people you have blocked.</div>

      <!-- EXCLUDED -->
      <div class="s__privacy" :class="EXCLUDED.list.length === 0 ? 's__privacy-container-empty' : ''" v-show="tab === 'EXCLUDED'">
        <div class="s__privacy-empty" v-if="EXCLUDED.list.length === 0 && EXCLUDED.firstLoad">You are currently not on a break from anyone.</div>
        <div class="s__privacy-loading" v-else-if="EXCLUDED.list.length === 0 && !EXCLUDED.firstLoad">Loading...</div>
        <div class="s__privacy-container" id="EXCLUDED" v-if="EXCLUDED.list.length !== 0">
          <div class="s__privacy-item" v-for="(excluded, index) in EXCLUDED.list" :key="index">
            <div class="s__profile-data" v-if="excluded.partner">
              <a :href="excluded.partner._id ? `/profile/${excluded.partner._id}` : ''">
                <ProfileImage :profileImageUrl="getProfileImage(excluded.partner)" :imageHeight="40" :imageWidth="40" :marginRight="12" height="300px" width="300px" />
              </a>
              <div class="s__profile-meta">
                <a :href="`/profile/${excluded.partner._id}`" class="s__privacy-name">{{ excluded.partner.displayName }}</a>
                <span class="s__privacy-desc" v-if="excluded.expireAt">Break set to end on {{ getDatetimeString(excluded.expireAt) }}</span>
                <span class="s__privacy-desc" v-else>Break set to end when you cancel it</span>
              </div>
            </div>
            <SettingsAreaPrivacyOptionsButton @unexclude="unexclude(excluded)" @block="initBlock(excluded)" @editExclusion="initEditExclusion(excluded)" type="excluded" />
          </div>
        </div>
        <div class="s__load-more" v-if="!EXCLUDED.allLoaded && EXCLUDED.firstLoad" @click="getUserBlockedOrExcludedData">{{ loading ? "Loading..." : "Load more" }}</div>
        <div class="s__all-loaded" v-else-if="EXCLUDED.allLoaded && EXCLUDED.list.length > 0"></div>
      </div>

      <!-- BLOCKED -->
      <div class="s__privacy" :class="BLOCKED.list.length === 0 ? 's__privacy-container-empty' : ''" v-show="tab === 'BLOCKED'">
        <div class="s__privacy-empty" v-if="BLOCKED.list.length === 0 && BLOCKED.firstLoad">Looks like you have not blocked anyone.</div>
        <div class="s__privacy-loading" v-else-if="BLOCKED.list.length === 0 && !BLOCKED.firstLoad">Loading...</div>
        <div class="s__privacy-container" id="BLOCKED" v-if="BLOCKED.list.length !== 0">
          <div class="s__privacy-item" v-for="(blocked, index) in BLOCKED.list" :key="index">
            <div class="s__profile-data">
              <ProfileImage :profileImageUrl="getProfileImage(blocked.partner)" :imageHeight="40" :imageWidth="40" :marginRight="12" height="300px" width="300px" />
              <div class="s__profile-meta">
                <div class="s__privacy-name s__blocked-name">{{ blocked.partner.displayName }}</div>
              </div>
            </div>
            <SettingsAreaPrivacyOptionsButton @unblock="unblock(blocked)" type="blocked" />
          </div>
        </div>
        <div class="s__load-more" v-if="!BLOCKED.allLoaded && BLOCKED.firstLoad" @click="getUserBlockedOrExcludedData">{{ loading ? "Loading..." : "Load more" }}</div>
        <div class="s__all-loaded" v-else-if="BLOCKED.allLoaded && BLOCKED.list.length > 0"></div>
      </div>
    </div>
  </div>
</template>

<script>
/* import Vue from "vue"; */
import { mapState } from "vuex";
import profileAPIService from "../../api/profileAPIService";
import SettingsAreaPrivacyOptionsButton from "./SettingsAreaPrivacyOptionsButton.vue";
import ProfileImage from "../../components/ProfileImage.vue";
import { formatExclusionDate } from "@/utilities/dateFormatter";
import BlockExcludeModal from "../../pages/ProfilePages/ProfileComponents/BlockExcludeModal.vue";
import BlockExcludeSuccessModal from "../../pages/ProfilePages/ProfileComponents/BlockExcludeSuccessModal.vue";

export default {
  name: "Privacy",
  computed: {
    ...mapState({
      user: (state) => state.auth.user,
      blocking: (state) => state.modals.blocking,
      excluding: (state) => state.modals.excluding,
      blockingComplete: (state) => state.modals.blockingComplete,
      excludingComplete: (state) => state.modals.excludingComplete,
      unexcludingComplete: (state) => state.modals.unexcludingComplete,
      notifications: (state) => state.notifier.notifications,
    }),

    /* Limit length of name to avoid messing up styles */
    firstName() {
      if (this.displayName) {
        let string = this.displayName;
        let isLongString = false;
        if (string.length > 18) {
          isLongString = true;
        }
        string = string.replace(/ .*/, "").substring(0, 17);
        return isLongString ? string + "..." : string;
      } else {
        return this.displayName;
      }
    },
  },
  components: {
    SettingsAreaPrivacyOptionsButton,
    ProfileImage,
    BlockExcludeModal,
    BlockExcludeSuccessModal,
  },
  data() {
    return {
      blocked: [],
      excluded: [],
      tab: "EXCLUDED",
      lastQueriedBlocked: null,
      lastQueriedExcluded: null,
      blockedLoaded: false,
      excludedLoaded: false,

      loading: false,

      BLOCKED: {
        list: [],
        lastQueried: null,
        allLoaded: false,
        firstLoadDone: false,
      },
      EXCLUDED: {
        list: [],
        lastQueried: null,
        allLoaded: false,
        firstLoadDone: false,
      },

      displayName: "",
      selectedID: "",
      breakEndDate: "",

      blockingInProcess: false,
      excludingInProcess: false,

      notififierMessages: {
        blocked: "Blocking successful!",
        unblocked: "Block removed!",
        breakCancelled: "Break cancelled!",
        breakUpdated: "Break updated!",
      },
    };
  },
  async mounted() {
    if (this.$route && this.$route.query && this.$route.query.type) {
      this.tab = this.$route.query.type.toUpperCase();
    }
    await this.switchTab();
  },
  methods: {
    /* Tab could change, as such we pass it */
    async getUserBlockedOrExcludedData() {
      const type = this.tab;
      if (type !== "EXCLUDED" && type !== "BLOCKED") return console.log("error");
      this.loading = true;
      let lastQueriedDate = this[type]["lastQueried"];
      if (lastQueriedDate === null) {
        lastQueriedDate = new Date();
      }
      const result = await profileAPIService.getUserBlockedOrExcludedData(this.user._id, type, lastQueriedDate);
      if (result && result.success && result.blockedOrExcludedData) {
        if (result.blockedOrExcludedData.length) {
          this[type]["list"] = [...this[type]["list"], ...result.blockedOrExcludedData];

          /* When <10 is returned, which is the maximum, then no more documents are to be found */
          if (result.blockedOrExcludedData.length < 10) {
            this[type]["allLoaded"] = true;
          }
          /* Set new last queried item */
          const list = this[type]["list"];
          const lastItem = list[list.length - 1];
          this[type]["lastQueried"] = new Date(lastItem.createdAt);

          /* If user loaded more and not the first load,
          scroll to the bottom to show what was loaded,
          as it can be hidden from view */
          if (this[type]["firstLoad"]) {
            this.$nextTick(() => {
              const element = document.getElementById(type);
              element.scrollTo({
                top: element.scrollHeight,
                left: 0,
                behavior: "smooth",
              });
            });
          }
        } else {
          /* If no documents are found at all, list is complete */
          this[type]["allLoaded"] = true;
        }
        if (!this[type]["firstLoad"]) this[type]["firstLoad"] = true;
        this.loading = false;
      }
    },

    async unexclude(excluded) {
      if (!excluded || !excluded.UIDs || !excluded.UIDs.length || excluded.UIDs.length !== 2) return;
      const docID = excluded._id;
      const excluderID = this.user._id;
      const excludedID = excluded.UIDs[0] === excluderID ? excluded.UIDs[1] : excluded.UIDs[0];
      const result = await profileAPIService.unexcludeUser(excluderID, excludedID);
      if (result && result.success) {
        const index = this.EXCLUDED.list.findIndex((obj) => obj._id === docID);
        this.EXCLUDED.list.splice(index, 1);
        const identifier = this.addNotification(this.notififierMessages.breakCancelled);
        await this.delay(2000);
        this.removeNotification(identifier);
      }
    },

    async unblock(blocked) {
      if (!blocked || !blocked.UIDs || !blocked.UIDs.length || blocked.UIDs.length !== 2) return;
      const docID = blocked._id;
      const blockerID = this.user._id;
      const blockedID = blocked.UIDs[0] === blockerID ? blocked.UIDs[1] : blocked.UIDs[0];
      const result = await profileAPIService.unblockUser(blockerID, blockedID);
      if (result && result.success) {
        const index = this.BLOCKED.list.findIndex((obj) => obj._id === docID);
        this.BLOCKED.list.splice(index, 1);
        const identifier = this.addNotification(this.notififierMessages.unblocked);
        await this.delay(2000);
        this.removeNotification(identifier);
      }
    },

    async block() {
      this.blockingInProcess = true;
      if (!this.user._id || !this.selectedID) return console.log("missing data");
      const result = await profileAPIService.blockUser(this.user._id, this.selectedID);
      if (!result) return console.log("missing result");
      this.$store.dispatch("modals/setModalState", {
        modal: "blocking",
        newState: false,
      });
      this.blockingInProcess = false;

      const index = this.EXCLUDED.list.findIndex((obj) => obj.partner._id === this.selectedID);
      if (!index && index !== 0) return console.log("missing index");
      const excludedDoc = this.EXCLUDED.list[index];
      if (!excludedDoc) return console.log("missing doc");
      this.EXCLUDED.list.splice(index, 1);
      this.BLOCKED.list.unshift(excludedDoc);

      this.displayName = "";
      this.selectedID = "";

      const identifier = this.addNotification(this.notififierMessages.blocked);
      await this.delay(2000);
      this.removeNotification(identifier);
    },

    async updateExclusion(ms) {
      this.excludingInProcess = true;
      const update = await profileAPIService.excludeUser(this.user._id, this.selectedID, ms, true);
      if (update && update.success && update.result && update.result._id) {
        this.$store.dispatch("modals/setModalState", {
          modal: "excluding",
          newState: false,
        });
        this.excludingInProcess = false;
        const index = this.EXCLUDED.list.findIndex((obj) => obj._id === update.result._id);
        const copy = JSON.parse(JSON.stringify(this.EXCLUDED.list[index])); // copy to get 'partner' field
        if (update.result.expireAt) {
          copy.expireAt = update.result.expireAt;
        } else {
          if (copy.expireAt) {
            delete copy.expireAt;
          }
        }
        this.$set(this.EXCLUDED.list, index, copy);

        this.displayName = "";
        this.selectedID = "";
        this.breakEndDate = "";
        const identifier = this.addNotification(this.notififierMessages.breakUpdated);
        await this.delay(2000);
        this.removeNotification(identifier);
      }
    },

    async initEditExclusion(excluded) {
      this.displayName = excluded.partner.displayName;
      this.selectedID = excluded.partner._id;
      this.breakEndDate = excluded.expireAt ? this.getDatetimeString(excluded.expireAt) : "";
      this.$store.dispatch("modals/setModalState", {
        modal: "excluding",
        newState: true,
      });
    },

    async initBlock(excluded) {
      this.displayName = excluded.partner.displayName;
      this.selectedID = excluded.partner._id;
      this.$store.dispatch("modals/setModalState", {
        modal: "blocking",
        newState: true,
      });
    },

    async switchTab(tab = null) {
      if (tab) {
        this.tab = tab;
      } else {
        tab = this.tab;
      }
      if (!this[tab]["allLoaded"] && !this[tab]["firstLoadDone"]) {
        await this.getUserBlockedOrExcludedData();
      }
    },

    getProfileImage(partner) {
      return partner.profileImage && partner.profileImage.fileUrl ? partner.profileImage.fileUrl : "";
    },

    getDatetimeString(datetime) {
      return formatExclusionDate(new Date(datetime));
    },

    async delay(ms) {
      return new Promise((res) => setTimeout(res, ms));
    },

    addNotification(notification) {
      const identifier = Date.now();
      this.$store.dispatch("notifier/addNewNotification", { notification, identifier });
      return identifier; // used later to remove notification
    },

    removeNotification(identifier) {
      this.$store.dispatch("notifier/removeNotification", identifier);
    },
  },
};
</script>

<style scoped>
.settingsAreaPrivacy {
  height: 100%;
}
.s__area {
  padding: 15px 55px !important;
}

.s__container {
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.s__options {
  display: flex;
  margin-bottom: 15px;
  border-radius: 16px;
  box-sizing: border-box;
}

.s__privacy-container {
  min-height: 300px;
  max-height: 510px;
  box-sizing: border-box;
  overflow-y: scroll;
  padding: 10px;
  height: 100%;
  display: flex;
  flex-direction: column;
}

.s__privacy {
  border-radius: 16px;
  min-height: 300px;
  max-width: 650px;
  position: relative;
  box-shadow: 0 0 0 1px rgb(15 15 15 / 5%), 0 3px 6px rgb(15 15 15 / 10%), 0 9px 24px rgb(213 213 213 / 20%);
  background-color: rgb(255 255 255 / 0.8);
  height: 100%;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
}

.s__desc {
  font-size: 15px;
  margin-bottom: 20px;
  color: #555;
}

.s__option {
  border-radius: 16px;
  padding: 8px 12px;
  font-weight: 500;
  font-size: 16px;
  margin-right: 6px;
  cursor: pointer;
  color: #333;
}

.s__option:hover,
.s__option-selected {
  background: white;
  background-color: rgba(255, 255, 255, 0.5);
  -webkit-backdrop-filter: blur(5px);
  backdrop-filter: blur(5px);
  box-shadow: 0 0 0 1px rgb(15 15 15 / 4%), 0 2px 4px rgb(15 15 15 / 7%), 0 4px 8px rgb(213 213 213 / 15%);
}

.s__privacy-item {
  display: flex;
  padding: 15px 15px;
  position: relative;
  box-sizing: border-box;
  width: 100%;
  margin: 0 auto;
  border-radius: 16px;
  margin-bottom: 5px;
}

.s__privacy-item:hover {
  box-shadow: 0 0 0 1px rgb(15 15 15 / 5%), 0 3px 6px rgb(15 15 15 / 10%), 0 9px 24px rgb(213 213 213 / 20%);
}

.s__profile-data {
  display: flex;
  align-items: center;
}

.s__profile-meta {
  display: flex;
  flex-direction: column;
}

.s__privacy-name {
  font-size: 16px;
  font-weight: 500;
  color: #333;
}

.s__privacy-name:hover {
  text-decoration: underline;
  text-underline-offset: 2px;
  text-decoration-thickness: 1px;
}

.s__blocked-name {
  cursor: default;
}

.s__blocked-name:hover {
  text-decoration: none;
}

.s__privacy-desc {
  font-size: 14px;
  margin-top: 4px;
  color: #444;
}

.s__privacy-cancel {
  margin-left: auto;
  font-size: 14px;
}

.s__load-more {
  margin: 0 auto;
  width: 100px;
  border-radius: 360px;
  padding: 10px 10px;
  font-size: 16px;
  cursor: pointer;
  background-color: #111;
  color: white;
  text-align: center;
  font-weight: 600;
  position: absolute;
  bottom: 20px;
  left: 0;
  right: 0;
}

.s__all-loaded {
  margin: 0 auto;
  width: 100px;
  border-radius: 360px;
  padding: 10px 10px;
  font-size: 13px;
  text-align: center;
  font-weight: 600;
  position: absolute;
  bottom: 10px;
  left: 0;
  right: 0;
}

.s__privacy-container-empty {
  display: flex;
  justify-content: center;
  align-items: center;
}

.s__privacy-empty,
.s__privacy-loading {
  text-align: center;
  font-size: 18px;
  color: #333;
}
@media (max-width: 749px) {
  .s__privacy {
    max-width: unset;
  }
}
</style>
